import React, { FC } from 'react';
import { useAuth } from '../../context/AuthContext';

interface AccessControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  reason: 'authenticated' | 'subscribed' | 'enrolled' | 'none' | 'error';
  message: string;
  onLogin?: () => void;
  onSubscribe?: () => void;
  onEnroll?: () => void;
  isEnrolling?: boolean;
}

const AccessControlModal: FC<AccessControlModalProps> = ({
  isOpen,
  onClose,
  reason,
  message,
  onLogin,
  onSubscribe,
  onEnroll,
  isEnrolling = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isAuthenticated } = useAuth();

  if (!isOpen) return null;

  const getModalContent = () => {
    switch (reason) {
      case 'authenticated':
        return {
          title: 'Login Required',
          icon: '🔐',
          description: 'You need to be logged in to access this feature.',
          primaryAction: {
            text: 'Login / Sign Up',
            action: onLogin,
            className: 'bg-blue-600 hover:bg-blue-700 text-white'
          },
          secondaryAction: {
            text: 'Cancel',
            action: onClose,
            className: 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }
        };
      
      case 'subscribed':
        return {
          title: 'Subscription Required',
          icon: '👑',
          description: 'This is a premium feature. Please subscribe to access live sessions and premium content.',
          primaryAction: {
            text: 'Subscribe Now',
            action: onSubscribe,
            className: 'bg-[#0284C7] hover:bg-blue-700 text-white'
          },
          secondaryAction: {
            text: 'Maybe Later',
            action: onClose,
            className: 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }
        };
      
      case 'enrolled':
        return {
          title: 'Enrollment Required',
          icon: '📚',
          description: 'You need to enroll in this course to access the classroom and live sessions.',
          primaryAction: {
            text: isEnrolling ? 'Enrolling...' : 'Enroll Now',
            action: onEnroll,
            className: 'bg-green-600 hover:bg-green-700 text-white',
            disabled: isEnrolling
          },
          secondaryAction: {
            text: 'Cancel',
            action: onClose,
            className: 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          }
        };
      
      case 'error':
        return {
          title: 'Error',
          icon: '⚠️',
          description: message || 'An error occurred while checking access permissions.',
          primaryAction: {
            text: 'OK',
            action: onClose,
            className: 'bg-red-600 hover:bg-red-700 text-white'
          },
          secondaryAction: null
        };
      
      default:
        return {
          title: 'Access Denied',
          icon: '❌',
          description: message || 'You do not have permission to access this feature.',
          primaryAction: {
            text: 'OK',
            action: onClose,
            className: 'bg-gray-600 hover:bg-gray-700 text-white'
          },
          secondaryAction: null
        };
    }
  };

  const content = getModalContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-3">{content.icon}</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {content.title}
          </h2>
          <p className="text-gray-600">
            {content.description}
          </p>
        </div>

        {/* Additional message */}
        {message && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">{message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col space-y-3">
          <button
            onClick={content.primaryAction.action}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${content.primaryAction.className}`}
          >
            {content.primaryAction.text}
          </button>
          
          {content.secondaryAction && (
            <button
              onClick={content.secondaryAction.action}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${content.secondaryAction.className}`}
            >
              {content.secondaryAction.text}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AccessControlModal;
